import React from "react";

import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import useTranslations from "@/shared/lang/useTranslations";

import type { RecipientMode } from "../../types/dispersions-api.types";

export interface RecipientModeSwitchProps {
  mode: RecipientMode;
  onModeChange: (mode: RecipientMode) => void;
}

/**
 * "Send to a registered client" toggle for the recipient step — switches the panel between the manual
 * destination fields and the registered-client selects.
 *
 * **The polarity is the point.** Typing the recipient is the default, so it is the UNCHECKED state and the
 * switch names what you opt into. It read the other way around while an unregistered recipient was treated as
 * an exception: the label described the state, the hint called the method exceptional, and the operator had
 * to turn something on to do the ordinary thing. A switch whose default is on reads as a setting somebody
 * already changed.
 *
 * One line under it at all times, naming the consequence of the CURRENT position — a toggle that explains
 * only one of its two states leaves the other to be guessed. Both strings are one line at the step's width,
 * so toggling does not reflow the fields below.
 *
 * Built on the DS `ColocaSwitch`; the wrapping <label> keeps the text in the hit area (the switch root is a
 * labelable button, so clicking the label toggles it).
 */
export const RecipientModeSwitch: React.FC<RecipientModeSwitchProps> = ({ mode, onModeChange }) => {
  const { t } = useTranslations("dispersions");
  const useRegisteredClient = mode === "client";

  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex w-fit cursor-pointer items-center gap-3">
        <ColocaSwitch
          checked={useRegisteredClient}
          onCheckedChange={(checked) => onModeChange(checked ? "client" : "manual")}
        />
        <span className="text-sm font-semibold text-foreground">{t("recipient_use_client_label")}</span>
      </label>
      <p className="pl-11 text-sm text-muted-foreground">
        {t(useRegisteredClient ? "recipient_use_client_hint" : "recipient_manual_hint")}
      </p>
    </div>
  );
};
